import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { clientService } from '../../services/client.service';
import { FaPhone, FaEnvelope, FaWhatsapp, FaEdit, FaFilter, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const MyClients = () => {
    const [clients, setClients] = useState([]);
    const [allClients, setAllClients] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusUpdate, setStatusUpdate] = useState({
        status: '',
        notes: ''
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    useEffect(() => {
        fetchClients(false); // Don't reset page on mount
        fetchStats();
    }, [filter, searchTerm]);

    // Reset page only when filter or search changes (not on status update)
    useEffect(() => {
        setCurrentPage(1);
    }, [filter, searchTerm]);

    const fetchClients = async (resetPage = false) => {
        try {
            setLoading(true);
            const params = filter !== 'all' ? { status: filter } : {};
            const data = await clientService.getMyClients(params);
            const clientsList = data.clients || [];

            // Apply search filter
            const filtered = searchTerm
                ? clientsList.filter(c =>
                    c.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    c.contactPerson?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    c.city?.toLowerCase().includes(searchTerm.toLowerCase())
                )
                : clientsList;

            setAllClients(filtered);

            // Only reset page if explicitly told to
            if (resetPage) {
                setCurrentPage(1);
            }
        } catch (error) {
            toast.error('Failed to fetch clients');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const data = await clientService.getClientStats();
            setStats(data.stats || {});
        } catch (error) {
            console.error('Failed to fetch stats');
        }
    };

    const handleQuickAction = async (clientId, actionType) => {
        try {
            const interactionData = {
                type: actionType,
                notes: `${actionType} initiated`,
                outcome: 'neutral'
            };

            await clientService.addInteraction(clientId, interactionData);
            toast.success(`${actionType} action recorded`);
            fetchClients(false); // Don't reset page
        } catch (error) {
            toast.error('Failed to record action');
        }
    };

    const handleStatusUpdate = async () => {
        if (!selectedClient || !statusUpdate.status) {
            toast.error('Please select a status');
            return;
        }

        try {
            await clientService.updateClientStatus(
                selectedClient._id,
                statusUpdate.status,
                statusUpdate.notes
            );
            toast.success('Status updated successfully');
            setShowStatusModal(false);
            setSelectedClient(null);
            setStatusUpdate({ status: '', notes: '' });
            fetchClients(false); // DON'T reset page - stay on current page
            fetchStats();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            interested: 'bg-green-100 text-green-800',
            not_interested: 'bg-red-100 text-red-800',
            call_not_picked: 'bg-gray-100 text-gray-800',
            repitch_required: 'bg-yellow-100 text-yellow-800',
            whatsapp_sent: 'bg-blue-100 text-blue-800',
            mail_sent: 'bg-purple-100 text-purple-800',
            pending: 'bg-orange-100 text-orange-800',
            converted: 'bg-teal-100 text-teal-800',
            lost: 'bg-gray-100 text-gray-500'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    const formatStatus = (status) => {
        return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    // Pagination calculations
    const totalClients = allClients.length;
    const totalPages = Math.ceil(totalClients / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentClients = allClients.slice(startIndex, endIndex);
    const displayStart = totalClients === 0 ? 0 : startIndex + 1;
    const displayEnd = Math.min(endIndex, totalClients);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                pages.push('...');
                pages.push(totalPages);
            }
        }

        return pages;
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold">My Clients</h1>
                <p className="text-gray-600 mt-2">Total: {totalClients} assigned clients</p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Total Clients</p>
                    <p className="text-3xl font-bold text-blue-600 mt-2">{stats.totalClients || 0}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Interested</p>
                    <p className="text-3xl font-bold text-green-600 mt-2">{stats.interested || 0}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Pending</p>
                    <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.pending || 0}</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow">
                    <p className="text-gray-500 text-sm">Today's Follow-ups</p>
                    <p className="text-3xl font-bold text-purple-600 mt-2">{stats.todayFollowUps || 0}</p>
                </div>
            </div>

            {/* Search & Filter */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">Search</label>
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search company, contact, city..."
                            className="w-full px-3 py-2 border rounded-lg"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Filter by Status</label>
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="all">All Clients</option>
                            <option value="pending">Pending</option>
                            <option value="interested">Interested</option>
                            <option value="not_interested">Not Interested</option>
                            <option value="call_not_picked">Call Not Picked</option>
                            <option value="repitch_required">Repitch Required</option>
                            <option value="whatsapp_sent">WhatsApp Sent</option>
                            <option value="mail_sent">Mail Sent</option>
                            <option value="converted">Converted</option>
                        </select>
                    </div>

                    <div className="flex items-end">
                        <button
                            onClick={() => {
                                setFilter('all');
                                setSearchTerm('');
                            }}
                            className="w-full px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            </div>

            {/* Clients Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="mt-2">Loading clients...</p>
                    </div>
                ) : currentClients.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        No clients found
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Contact</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y">
                                    {currentClients.map((client) => (
                                        <tr key={client._id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div>
                                                    <p className="font-medium">{client.companyName}</p>
                                                    <p className="text-sm text-gray-500">{client.contactPerson}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm">
                                                    <p className="flex items-center gap-2">
                                                        <FaPhone className="text-gray-400" /> {client.phone || 'N/A'}
                                                    </p>
                                                    <p className="flex items-center gap-2 mt-1">
                                                        <FaEnvelope className="text-gray-400" /> {client.email || 'N/A'}
                                                    </p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                <p>{client.city}, {client.state}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800 capitalize">
                                                    {client.clientType?.replace('_', ' ')}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 text-xs rounded ${getStatusColor(client.status)}`}>
                                                    {formatStatus(client.status)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                {client.lastContactDate
                                                    ? new Date(client.lastContactDate).toLocaleDateString()
                                                    : 'Never'}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleQuickAction(client._id, 'call')}
                                                        className="p-2 text-green-600 hover:bg-green-50 rounded"
                                                        title="Call"
                                                    >
                                                        <FaPhone />
                                                    </button>
                                                    <button
                                                        onClick={() => handleQuickAction(client._id, 'email')}
                                                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                                        title="Email"
                                                    >
                                                        <FaEnvelope />
                                                    </button>
                                                    <button
                                                        onClick={() => handleQuickAction(client._id, 'whatsapp')}
                                                        className="p-2 text-green-600 hover:bg-green-50 rounded"
                                                        title="WhatsApp"
                                                    >
                                                        <FaWhatsapp />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setSelectedClient(client);
                                                            setShowStatusModal(true);
                                                        }}
                                                        className="p-2 text-purple-600 hover:bg-purple-50 rounded"
                                                        title="Update Status"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="bg-gray-50 px-6 py-4 border-t flex items-center justify-between">
                                <div className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{displayStart}</span> to{' '}
                                    <span className="font-medium">{displayEnd}</span> of{' '}
                                    <span className="font-medium">{totalClients}</span> results
                                </div>

                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <FaChevronLeft />
                                    </button>

                                    {getPageNumbers().map((page, index) => (
                                        <button
                                            key={index}
                                            onClick={() => typeof page === 'number' && handlePageChange(page)}
                                            className={`px-3 py-1 border rounded ${page === currentPage
                                                ? 'bg-blue-600 text-white'
                                                : page === '...'
                                                    ? 'cursor-default'
                                                    : 'hover:bg-gray-100'
                                                }`}
                                            disabled={page === '...'}
                                        >
                                            {page}
                                        </button>
                                    ))}

                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <FaChevronRight />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Update Status Modal */}
            {showStatusModal && selectedClient && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md w-full">
                        <h2 className="text-2xl font-bold mb-4">Update Status</h2>
                        <p className="text-gray-600 mb-4">Client: {selectedClient.companyName}</p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">New Status *</label>
                                <select
                                    value={statusUpdate.status}
                                    onChange={(e) => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                    required
                                >
                                    <option value="">Select Status</option>
                                    <option value="interested">Interested</option>
                                    <option value="not_interested">Not Interested</option>
                                    <option value="call_not_picked">Call Not Picked</option>
                                    <option value="repitch_required">Repitch Required</option>
                                    <option value="whatsapp_sent">WhatsApp Sent</option>
                                    <option value="mail_sent">Mail Sent</option>
                                    <option value="pending">Pending</option>
                                    <option value="converted">Converted</option>
                                    <option value="lost">Lost</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">Notes</label>
                                <textarea
                                    value={statusUpdate.notes}
                                    onChange={(e) => setStatusUpdate({ ...statusUpdate, notes: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2"
                                    rows="3"
                                    placeholder="Add notes about this update..."
                                ></textarea>
                            </div>

                            <div className="flex gap-2">
                                <button
                                    onClick={handleStatusUpdate}
                                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                                >
                                    Update Status
                                </button>
                                <button
                                    onClick={() => {
                                        setShowStatusModal(false);
                                        setSelectedClient(null);
                                        setStatusUpdate({ status: '', notes: '' });
                                    }}
                                    className="flex-1 bg-gray-300 py-2 rounded-lg hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyClients;